import { MOTION } from '@/components/common/motion';
import { DROP_ZONE, DROP_ZONE_ACTIVE } from '@/components/common/surfaces';
import { extractDroppedFiles } from '@/utils/dropFiles';
import { validateStpFile } from '@/utils/validators';
import { UploadSimple } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/** Layout of the drop target, on top of the shared `DROP_ZONE` look. */
const DROP_ZONE_LAYOUT =
  'flex h-[200px] w-full cursor-pointer flex-col items-center justify-center gap-4 px-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2';

interface ModelUploadZoneProps {
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  /** The step the background task reports, when one is being tracked here rather than by the overlay. */
  currentStep?: string;
}

export const ModelUploadZone: FC<ModelUploadZoneProps> = ({ onUpload, isUploading, currentStep = '' }) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const { files, hadDirectory, receivedAnything } = extractDroppedFiles(e.dataTransfer);
    const stpFile = files.find((f) => f.name.toLowerCase().endsWith('.stp'));

    if (!stpFile) {
      if (hadDirectory) {
        toast.error(t('validation.dropFolderStp'));
      } else if (!receivedAnything) {
        toast.error(t('validation.dropNoFileReceived'));
      } else {
        toast.error(t('validation.stpFileRequired'));
      }
      return;
    }

    const validation = validateStpFile(stpFile);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    await onUpload(stpFile);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateStpFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    await onUpload(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div
      className={`${isDragging ? DROP_ZONE_ACTIVE : DROP_ZONE} ${DROP_ZONE_LAYOUT} ${MOTION}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => !isUploading && fileInputRef.current?.click()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isUploading) {
          fileInputRef.current?.click();
        }
      }}
      tabIndex={0}
      role="button"
    >
      {isUploading ? (
        <div className="flex w-full max-w-xs flex-col gap-4">
          {/* Indeterminate rather than a percentage: the STEP is handed to a background task, and
              the only progress this zone can honestly report is "still going". */}
          <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="indeterminate-progress-bar h-full rounded-full bg-primary motion-reduce:animate-none" />
          </div>
          <p className="m-0 text-center text-sm text-gray-500">{currentStep || t('models.uploading')}</p>
        </div>
      ) : (
        <>
          <UploadSimple size={32} className={isDragging ? 'text-primary' : 'text-gray-400'} aria-hidden="true" />
          <div className="text-center">
            <p className="m-0 text-sm font-medium text-gray-900">
              {isDragging ? t('projects.dropHere') : t('models.uploadModel')}
            </p>
            <p className="m-0 text-xs text-gray-500">{t('projects.dragDrop')}</p>
          </div>
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".stp"
        className="hidden"
        onChange={handleFileSelect}
        disabled={isUploading}
      />
    </div>
  );
};
